(() => {
  const init = (root) => {
    if (!root || !root.matches('[data-tan-property-brief]') || root.dataset.propertyBriefReady) return;
    root.dataset.propertyBriefReady = 'true';

    const buttons = Array.from(root.querySelectorAll('[data-property-choice]'));
    const dateInput = root.querySelector('[data-property-date]');
    const distanceInput = root.querySelector('[data-property-distance]');
    const widthInput = root.querySelector('[data-property-width]');
    const contextHeading = root.querySelector('[data-property-context-heading]');
    const contextCopy = root.querySelector('[data-property-context-copy]');
    const contextMeta = root.querySelector('[data-property-context-meta]');
    const resultHeading = root.querySelector('[data-property-result-heading]');
    const resultCopy = root.querySelector('[data-property-result-copy]');
    const copyArea = root.querySelector('[data-property-copy-text]');
    const copyButton = root.querySelector('[data-property-copy]');
    const secondaryLink = root.querySelector('[data-property-secondary]');
    const i18nNode = root.querySelector('[data-property-brief-i18n]');

    const selected = {
      property: root.querySelector('[data-property-choice][data-choice-group="property"].is-active'),
      role: root.querySelector('[data-property-choice][data-choice-group="role"].is-active'),
      context: root.querySelector('[data-property-choice][data-choice-group="context"].is-active'),
    };

    const payload = (id) => root.querySelector(`[data-property-payload="${CSS.escape(id)}"]`);
    const clean = (value) => String(value || '').replace(/\s+/g, ' ').trim();
    const i18n = (() => {
      try {
        return JSON.parse(i18nNode?.textContent || '{}');
      } catch (_) {
        return {};
      }
    })();
    const translate = (key) => clean(i18n[key]);
    const interpolate = (key, values) => Object.entries(values).reduce(
      (output, [token, value]) => output.replaceAll(`TAN_${token.toUpperCase()}_TOKEN`, () => String(value ?? '')),
      translate(key),
    );
    const listText = (id) => {
      const template = payload(id);
      if (!template) return [];
      return Array.from(template.content.querySelectorAll('li')).map((item) => clean(item.textContent)).filter(Boolean);
    };

    const formatDate = () => {
      if (!dateInput?.value) return translate('notAdded');
      const parsed = new Date(`${dateInput.value}T12:00:00`);
      if (Number.isNaN(parsed.getTime())) return dateInput.value;
      return new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
        year: 'numeric', month: 'short', day: 'numeric',
      }).format(parsed);
    };

    const fieldState = () => {
      const distanceRaw = clean(distanceInput?.value);
      const widthRaw = clean(widthInput?.value);
      const selectedDistance = clean(distanceInput?.selectedOptions?.[0]?.textContent);
      return {
        date: formatDate(),
        distance: distanceRaw ? selectedDistance || distanceRaw : translate('notSelected'),
        width: widthRaw ? interpolate('widthValue', { value: widthRaw }) : translate('notAdded'),
      };
    };

    const updateContext = () => {
      const { date, distance, width } = fieldState();
      const hasSpatialData = Boolean(distanceInput?.value || widthInput?.value);

      if (contextHeading) {
        contextHeading.textContent = hasSpatialData
          ? root.dataset.contextReadyHeading
          : root.dataset.contextEmptyHeading;
      }
      if (contextCopy) {
        contextCopy.textContent = hasSpatialData
          ? root.dataset.contextReadyText
          : root.dataset.contextEmptyText;
      }
      if (contextMeta) {
        contextMeta.textContent = interpolate('contextMeta', { date, distance, width });
      }
    };

    const update = () => {
      const property = selected.property;
      const role = selected.role;
      const context = selected.context;
      if (!property || !role || !context) return;

      const propertyTitle = clean(property.dataset.choiceTitle);
      const roleTitle = clean(role.dataset.choiceTitle);
      const contextTitle = clean(context.dataset.choiceTitle);
      const combinedCopy = [property.dataset.choiceNote, role.dataset.choiceNote, context.dataset.choiceNote]
        .map(clean).filter(Boolean).join(' ');

      if (resultHeading) resultHeading.textContent = `${propertyTitle} · ${roleTitle} · ${contextTitle}`;
      if (resultCopy) resultCopy.textContent = combinedCopy;

      ['property', 'role', 'context'].forEach((group) => {
        const target = root.querySelector(`[data-property-list="${group}"]`);
        const source = payload(selected[group].dataset.choiceId);
        const content = source?.content.querySelector('[data-property-checklist]');
        if (target && content) target.innerHTML = content.innerHTML;
      });

      if (secondaryLink) {
        const label = clean(context.dataset.guideLabel);
        const link = clean(context.dataset.guideLink);
        secondaryLink.hidden = !(label && link);
        if (label && link) {
          secondaryLink.textContent = label;
          secondaryLink.href = link;
        }
      }

      if (copyArea) {
        const { date, distance, width } = fieldState();
        const lines = [
          translate('copyTitle'),
          interpolate('copyDate', { value: date }),
          interpolate('copyPrimary', { value: propertyTitle }),
          interpolate('copyRole', { value: roleTitle }),
          interpolate('copyContext', { value: contextTitle }),
          interpolate('copyDistance', { value: distance }),
          interpolate('copyWidth', { value: width }),
          '',
          interpolate('copyPlanning', { value: combinedCopy }),
          '',
          interpolate('copyPrimaryDetails', { value: listText(property.dataset.choiceId).join('; ') }),
          interpolate('copyRoleDetails', { value: listText(role.dataset.choiceId).join('; ') }),
          interpolate('copyContextDetails', { value: listText(context.dataset.choiceId).join('; ') }),
          '',
          translate('copyAttachments'),
        ];
        copyArea.value = lines.join('\n');
      }

      updateContext();
    };

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const group = button.dataset.choiceGroup;
        if (!group || !selected[group]) return;
        buttons.filter((item) => item.dataset.choiceGroup === group).forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', String(active));
        });
        selected[group] = button;
        update();
      });
    });

    [dateInput, distanceInput, widthInput].forEach((input) => input?.addEventListener('input', update));

    copyButton?.addEventListener('click', async () => {
      if (!copyArea) return;
      try {
        await navigator.clipboard.writeText(copyArea.value);
      } catch (_) {
        copyArea.focus();
        copyArea.select();
        document.execCommand('copy');
      }
      const original = copyButton.dataset.copyLabel || copyButton.textContent;
      copyButton.textContent = copyButton.dataset.copiedLabel || translate('briefCopied') || original;
      window.setTimeout(() => { copyButton.textContent = original; }, 1800);
    });

    update();
  };

  const initAll = (scope = document) => {
    if (scope.matches?.('[data-tan-property-brief]')) init(scope);
    scope.querySelectorAll?.('[data-tan-property-brief]').forEach(init);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
